<template>
  <NuxtLayout name="catalog">
    <div class="page-info__magazine">
      <div class="page-container">
        <MoleculesGlobalBreadcrumb :segments="getInfoPageSegments()" :show-all-segments="true" />
      </div>

      <div class="page-info__magazine__heading">
        <div class="page-info__magazine__wrapper">
          <h1>{{ entity.page.header }}</h1>

          <div class="page-info__magazine__meta">
            <span v-if="!!entity.page.publishedAt">
              <AtomsImagesIcon icon="calendar" class="mr-2 !align-baseline" />{{ getDate(entity.page.publishedAt) }}
            </span>
            <AtomsTextLink :href="`${cache.getModuleUrl('modules/magazine')}#!c=${entity.magazineCategory.id}`" class="underline text-primary-500">{{ entity.magazineCategory.name }}</AtomsTextLink>
            <span>
              <AtomsImagesIcon icon="user" class="mr-2 !align-baseline" />{{ entity.author }}
            </span>
            <span>
              <AtomsImagesIcon icon="clock" class="mr-2 !align-baseline" />{{ $t('Doba čtení') }} <strong>{{ $t('minuta', entity.timeToRead) }}</strong>
            </span>
            <span v-if="entity.opened">
              {{ $t('Přečteno') }}: {{ entity.opened }}x
            </span>
          </div>

        </div>
      </div>

      <div class="page-info__magazine__wrapper">
        <div class="page-info__magazine__content">

          <!-- dynamic blocks -->

          <div v-if="blocks" class="page-info__magazine__blocks">

            <template v-for="(block, index) in blocks">

              <p v-if="block.type === 'paragraph'" v-html="block.data.text" class="page-info__magazine__paragraph" :class="[getParagraphAlign(block.data.alignment), { 'page-info__magazine__paragraph--perex': index === 0 }]"></p>

              <component v-else-if="block.type === 'header'" :is="`h${block.data.level}`" class="page-info__magazine__header" :class="`page-info__magazine__header--h${block.data.level}`"><span v-html="block.data.text"></span></component>

              <MoleculesBlocksInfoPageElement v-else-if="block.type === 'raw'" :element="useContent().transformHtmlContent(block.data.html, page.id)[0]" />

              <template v-else-if="block.type === 'inspirationInfobox' && getInspirationForBlock(index)">
                <MoleculesDynamicInspirationPhoto :id="getInspirationForBlock(index).id" :file-type="getInspirationForBlock(index).fileType" :name="getInspirationForBlock(index).name" :links="getInspirationForBlock(index).links" data-pageelementname="photo_gallery_inspiration" :data-pageelementspecification="getInspirationForBlock(index).name" />
              </template>

              <template v-else-if="block.type === 'image'">
                <AtomsImagesImage :src="transformImageUrl(`${useShopZone().value.staticResourcesRoot}${block.data.file.urlSZ}`, 'base')" :webp="true" :alt="block.data.alt" class="page-info__magazine__image" @click="openLightBox(findImageIndex(block.data.file.urlSZ))" />
                <p v-if="block.data.caption" class="page-info__magazine__image__caption" v-html="block.data.caption"></p>
              </template>

              <component v-else-if="block.type === 'list'" :is="block.data.style === 'ordered' ? 'ol' : 'ul'" class="page-info__magazine__list">
                <li v-for="item in block.data.items" v-html="item"></li>
              </component>

              <template v-else-if="(block.type === 'categoryInfobox' || block.type === 'productsInfobox') && getItemsForBlock(index).length">
                <div :class="getItemsForBlock(index).length === 1 ? 'page-info__magazine__product' : 'page-info__magazine__products'" data-pageelementname="element_variable_box" :data-pageelementspecification="getItemsForBlock(index).length === 1 ? 'variable_box_product' : 'variable_box_category'">

                  <div v-if="getItemsForBlock(index).length === 1" class="page-info__magazine__product__header">
                    <span class="relative z-[1] px-4 bg-neutral-50">
                      {{ $t('Doporučený produkt') }}
                    </span>
                  </div>

                  <div v-else class="page-info__magazine__products__header">
                    <span class="relative z-[1] pr-4 bg-neutral-50">
                      {{ $t('Doporučené produkty') }}
                    </span>
                  </div>

                  <OrganismsListsWareList :items="getItemsForBlock(index)" :type="getItemsForBlock(index).length === 1 ? 'light-single' : 'article'" :with-slider-navigation="true"></OrganismsListsWareList>

                  <div v-if="block.type === 'categoryInfobox' && getGroupForBlock(index) && getItemsForBlock(index).length > 1" class="relative flex items-center justify-end gap-4 text-right before:content-[''] before:absolute before:left-0 before:right-0 before:border-b before:border-primary-500">
                    <span class="relative z-[1] pl-4 bg-neutral-50">
                      <MoleculesButtonsButton :href="getGroupForBlock(index).page.url" :ghost="true" :arrow-right="true" class="text-sm">{{ getGroupForBlock(index).name }}</MoleculesButtonsButton>
                    </span>
                  </div>

                </div>
              </template>

              <template v-else-if="block.type === 'vendorInfobox' && getGroupForBlock(index)">
                <div class="page-info__magazine__vendor" data-pageelementname="element_variable_box" data-pageelementspecification="variable_box_brand">

                  <div class="page-info__magazine__vendor__header">
                    <span class="relative z-[1] pr-4 bg-neutral-50">
                      {{ $t('O značce') }}
                    </span>
                  </div>

                  <div class="mt-4 mb-4 font-bold text-primary-500">{{ getGroupForBlock(index).name }}</div>
                  <div class="mb-4" v-html="getGroupForBlock(index).annotation"></div>

                  <div class="relative flex items-center justify-end gap-4 text-right before:content-[''] before:absolute before:left-0 before:right-0 before:border-b before:border-primary-500">
                    <span class="relative z-[1] pl-4 bg-neutral-50">
                      <MoleculesButtonsButton :href="getGroupForBlock(index).page.url" :ghost="true" :arrow-right="true" class="text-sm">{{ $t('Produkty') }} {{ getGroupForBlock(index).name }}</MoleculesButtonsButton>
                    </span>
                  </div>

                </div>
              </template>

              <table v-else-if="block.type === 'table' && block.data.content?.length">
                <tbody>
                  <tr v-for="row in block.data.content">
                    <td v-for="cell in row" v-html="cell"></td>
                  </tr>
                </tbody>
              </table>

            </template>
          </div>
          <div v-else v-html="rawContent"></div>
        </div>

        <!-- author -->

        <div v-if="authorName" class="my-8 p-6 border border-neutral-300 tablet:p-8">
          <div class="flex items-center gap-6 tablet:gap-8">
            <AtomsImagesImage v-if="false" :src="`https://kinoatlaspraha.cz/files/2023-08/thumb_900_230816010336-7855.jpg`" :alt="$t('Autor článku')" class="w-24 aspect-square object-cover border border-neutral-300 rounded-full tablet:w-32" />
            <div class="flex-1">
              <div class="text-sm uppercase text-neutral-300">{{ $t('Autor článku') }}</div>
              <div class="text-2xl font-bold tracking-normal">{{ authorName }}</div>
              <div class="hidden mt-2 text-sm tablet:block">
                {{ authorDescription }}
              </div>
            </div>
          </div>
          <div class="mt-6 text-sm tablet:hidden">
            {{ authorDescription }}
          </div>
        </div>

        <!-- share -->

        <div class="my-8 py-4 text-center border border-neutral-300 desktop:p-8">
          <div class="mb-8 uppercase">{{ $t('Líbí se vám článek?') }}</div>
          <MoleculesGlobalArticleShareIcons :article-header="entity.page.header" :article-analytics-data="props.articleAnalyticsData" />
        </div>

        <!-- tags -->

        <div v-if="entity.filterProperties?.length" class="page-info__magazine__tags">
          {{ $t('Štítky') }}
          <AtomsTextLink v-for="tag in entity.filterProperties" :to="`${cache.getModuleUrl('modules/magazine')}#!l=${tag.lValue}`" class="page-info__magazine__tags__item">{{ tag.value }}</AtomsTextLink>
        </div>

        <!-- articles -->

        <h2 class="page-info__magazine__header page-info__magazine__header--h2 !my-8">{{ $t('Čtenáře dále zajímá') }}</h2>

        <div class="flex flex-wrap gap-4">
          <MoleculesBlocksMagazineItem v-for="article in articles.slice(0, 2)" :date-published="article.page.publishedAt" :date-created="article.created" :title="article.page.title" :url="article.page.url" :annotation="article.annotation" :time-to-read="article.timeToRead" :item-id="article.id" class="w-full !flex-none tablet:!flex-1" />
        </div>

        <div class="!my-12 text-center">
          <MoleculesButtonsButton :ghost="true" :arrow-left="true" :href="cache.getPageUrl(18438)">{{ $t('Přejít do magazínu') }}</MoleculesButtonsButton>
        </div>

      </div>
    </div>
    <div class="page-container">

      <!-- recently viewed items -->

      <LazyTemplatesRecentlyViewedItems />

      <!-- usp -->

      <OrganismsBlocksUsps class="mb-4 desktop:mb-6" item-class="!bg-neutral-100 desktop:aspect-[7/3]" />

    </div>

    <LazyOrganismsDialogsInspirationDetail v-if="inspirationsData && Object.keys(inspirationsData).length" />

    <VueEasyLightbox :visible="lightBoxOpened" :index="lightBoxIndexRef" :imgs="lightBoxImages" :min-zoom="0.12" @hide="closeLightBox" />
  </NuxtLayout>
</template>
<script setup>

import VueEasyLightbox from '~/components/molecules/global/lightbox/index.ts'
import "assets/css/lightbox.css";

const props = defineProps({
  articleAnalyticsData: Object
});

const page = usePage().value.page;
const entity = useEntity();
const locale = useLocale().getLocale();
const cache = useCache();
const i18n = useI18n();

let blocks;
let rawContent;

try {
  blocks = JSON.parse(entity.content.replaceAll('"/shopFiles', `"${useShopZone().value.staticResourcesRoot}/shopFiles`)).blocks;
}
catch (err) {
  rawContent = entity.content.replaceAll('"/shopFiles', `"${useShopZone().value.staticResourcesRoot}/shopFiles`);
}

const getInfoPageSegments = () => {

  const result = entity.parents.map(parent => {
    return {
      level: parent.level,
      name: parent.page.id === 18438 ? i18n.t('Magazín') : (parent.page.id === 18437 ? i18n.t('Slovník pojmů') : parent.page.title),
      url: `${parent.page.url}`
    }
  });

  result.push({
    level: 0,
    name: entity.page.header
  });

  return result;
}

const getParagraphAlign = (alignment) => {
  switch (alignment) {
    case 'left':
      return 'text-left';
    case 'center':
      return 'text-center';
    case 'right':
      return 'text-right';
    default:
      return '';
  }
};

const getWareListItems = async (filter) => {

  const result = useState(`magazineArticleWareList-${JSON.stringify(filter)}`, () => null);

  if (!result.value) {
    result.value = (await useApiFetch(`/api/wareList?withoutParameters=true&currencyId=${locale.currencyId}&languageId=${locale.languageId}`,
      {
        method: 'POST',
        body: {
          filter: filter
        },
      })).wareList.wares.items;

  }

  return result.value;
}

const loadItems = async () => {
  const promises = blocks.map((block, index) => {
    if (block.type === 'categoryInfobox' || block.type === 'productsInfobox') {

      let filter;

      if (block.type === 'categoryInfobox') {
        filter = {
          groupId: { eq: block.data.idGroup }
        }
      }
      else {
        filter = {
          wareId: { in: block.data.idWare }
        }
      }

      Object.assign(filter, {
        and: {
          tags: [
            {
              type: 'STORED'
            }
          ],
          not: {
            tags: [
              {
                type: 'CLEARANCESALE'
              }
            ]
          }
        }
      });

      return getWareListItems(filter).then(result => ({ index, result }));
    }
    else {
      return { index, result: null };
    }
  });

  const results = await Promise.all(promises);

  const result = {};

  for (const { index, result: itemResult } of results) {
    if (itemResult) {
      result[index] = itemResult;
    }
  }

  return result;
}

const loadGroups = async () => {
  const promises = blocks.map((block, index) => {
    if (block.type === 'categoryInfobox' || block.type === 'vendorInfobox') {
      const parameterId = block.type === 'categoryInfobox' ? block.data.idGroup : block.data.idVendor;
      return useApiFetch(`/api/groupInfo?groupId=${parameterId}&languageId=${locale.languageId}&currencyId=${locale.currencyId}`)
        .then(response => ({ index, result: response.groups[0] }));
    }
    else {
      return { index, result: null };
    }
  });

  const results = await Promise.all(promises);

  const result = {};
  for (const { index, result: groupResult } of results) {
    if (groupResult) {
      result[index] = groupResult;
    }
  }

  return result;
}

const loadInspirations = async () => {
  const promises = blocks.map((block, index) => {
    if (block.type === 'inspirationInfobox') {
      const inspirationId = block.data.idInspiration;
      return useApiFetch(`/api/inspiration?id=${inspirationId}&languageId=${locale.languageId}&currencyId=${locale.currencyId}`)
        .then(response => ({ index, result: response.custom.ta.inspirationsList.inspirations.items[0] }));
    }
    else {
      return { index, result: null };
    }
  });

  const results = await Promise.all(promises);

  const result = {};
  for (const { index, result: inspirationResult } of results) {
    if (inspirationResult) {
      result[index] = inspirationResult;
    }
  }

  return result;
}

let itemsData;
let groupsData;
let inspirationsData;

if (blocks) {
  const promises = [];

  promises.push(loadItems().then(result => itemsData = result));
  promises.push(loadGroups().then(result => groupsData = result));
  promises.push(loadInspirations().then(result => inspirationsData = result));

  await Promise.all(promises);
}

const getItemsForBlock = (index) => {
  return itemsData[index.toString()];
}

const getGroupForBlock = (index) => {
  return groupsData[index.toString()];
}

const getInspirationForBlock = (index) => {
  return inspirationsData[index.toString()];
}

const getDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
};

const articles = (await useApiFetch(`/api/magazineArticlesDefault?currencyId=${locale.currencyId}&languageId=${locale.languageId}`)).infoPageList.infoPages.items.filter(q => q.id !== entity.id && q.page.publishedAt);

const removeTrailingLines = () => {
  var items = document.querySelectorAll('.page-info__magazine__meta > *');
  for (var i = 0; i < items.length; i++) {
    var current = items[i];
    var next = items[i + 1];
    if (next && current.getBoundingClientRect().top !== next.getBoundingClientRect().top) {
      current.classList.add('end-of-line');
    } else {
      current.classList.remove('end-of-line');
    }
  }
};

if (props.articleAnalyticsData) {
  useBaseAnalytics().pushEvent('article_view', props.articleAnalyticsData);
}

const authorName = page.structuredData.find(record => record.key === 'Article/author#Person/name')?.value;
const authorDescription = page.structuredData.find(record => record.key === 'Article/author#Person/description')?.value;

//#region lightbox

const lightBoxOpened = ref(false);
const lightBoxIndexRef = ref(0);
const lightBoxImages = computed(() => blocks?.length && blocks?.filter(block => block.type === 'image').map((item) => {
  return {
    src: transformImageUrl(`${useShopZone().value.staticResourcesRoot}${item.data.file.urlSZ}`, 'lg')
  }
}));

const openLightBox = (index) => {
  lightBoxIndexRef.value = index;
  lightBoxOpened.value = true;
}

const closeLightBox = () => lightBoxOpened.value = false;

const findImageIndex = (url) => {
  return blocks.filter(block => block.type === 'image').findIndex(block => block.type === 'image' && block.data.file.urlSZ === url);
}

// replace file extension with jpg
const transformImageUrl = (url, suffix) => {
  const result = url.replace(/\.[^/.]+$/, `_${suffix}.jpg`);
  return result;
}

//#endregion

useSeoMeta({
  ogImage: `${useShopZone().value.staticResourcesRoot}/img/magazine/${entity.id}.jpg`
});

onMounted(() => {
  removeTrailingLines();
  window.addEventListener('resize', removeTrailingLines);
});

onUnmounted(() => {
  window.removeEventListener('resize', removeTrailingLines);
});

</script>
<style lang="postcss">
.page-info__magazine {

  @apply text-black;

  &__content {

    .mag {
      &-perex {
        @apply my-4 text-[1.05rem] leading-6 text-justify font-bold;
      }
    }

    p {
      @apply leading-7 text-lg text-justify indent-6;
    }

    h2 {
      @apply my-4 font-bold tracking-normal text-2xl;
    }

    h3 {
      @apply my-4 font-bold tracking-normal text-lg;
    }

    a {
      @apply text-secondary-500 underline;
    }

    ul,
    ol {
      @apply my-4 leading-8 list-disc list-inside tablet:pl-10;
    }

    img {
      @apply inline-block my-4;
    }

    .warelist__item {
      @apply text-neutral-500 no-underline;
    }
  }

  &__heading {
    @apply my-8 py-8 bg-neutral-100;

    h1 {
      @apply text-4xl font-bold tracking-normal text-center;
    }
  }

  &__wrapper {
    @apply max-w-[740px] mx-auto px-4;
  }

  &__meta {
    @apply flex flex-wrap items-center justify-center gap-x-8 gap-y-2 mt-6 text-[0.95rem] text-neutral-400;

    &>* {
      @apply relative;
    }

    &>*:after {
      @apply absolute -right-4 top-1 bottom-0.5 content-[''] border-r border-neutral-300;
    }

    &>*:last-child:after {
      @apply hidden;
    }

    .end-of-line:after {
      @apply hidden;
    }
  }

  &__blocks {
    @apply space-y-4;
  }

  &__paragraph {

    @apply leading-7 text-lg text-justify indent-6;

    &--perex {
      @apply text-[1.05rem] tracking-normal font-bold indent-0;
    }
  }

  &__image {

    @apply mx-auto cursor-pointer;

    &__caption {
      @apply !mt-2 text-sm text-neutral-300;
    }
  }

  &__product {

    @apply !my-10 border border-t-0 border-primary-500;

    &__header {
      @apply text-lg uppercase tracking-normal text-primary-500 relative -top-3.5 flex items-center justify-center text-right before:content-[''] before:absolute before:left-0 before:right-0 before:border-b before:border-primary-500;
    }
  }

  &__products {

    @apply !my-10;

    &__header {
      @apply text-lg uppercase tracking-normal text-primary-500 relative flex items-center before:content-[''] before:absolute before:left-0 before:right-0 before:border-b before:border-primary-500;
    }
  }

  &__vendor {

    @apply !my-10;

    &__header {
      @apply text-lg uppercase tracking-normal text-primary-500 relative flex items-center before:content-[''] before:absolute before:left-0 before:right-0 before:border-b before:border-primary-500;
    }
  }

  &__tags {
    @apply flex flex-wrap items-center gap-4 my-4 px-4 py-6 uppercase border-t border-b border-neutral-300;

    &__item {
      @apply block px-4 py-2 text-primary-500 border border-primary-500 hover:no-underline;
    }
  }

  &__header {
    @apply my-4 font-bold tracking-normal text-2xl;
  }
}
</style>